import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Download, FileText, ShoppingBag, Loader2, Eye, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { IMAGES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { id: "tutti", label: "Tutti" },
  { id: "gratuite", label: "Mini Guide Gratuite" },
  { id: "premium", label: "Ebook & Workbook" },
];

export const Resources = () => {
  const [guides, setGuides] = useState([]);
  const [tab, setTab] = useState("tutti");
  const [buying, setBuying] = useState(null);
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [lead, setLead] = useState(null);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadNews, setLeadNews] = useState(true);
  const [leadSending, setLeadSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/guides`).then((r) => setGuides(r.data.guides)).catch(() => {
      toast.error("Impossibile caricare le risorse. Riprova tra poco.");
    });
  }, []);

  const filtered = guides.filter((g) =>
    tab === "tutti" ? true : tab === "gratuite" ? g.free : !g.free
  );

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadSending(true);
    try {
      await axios.post(`${API}/guides/${lead.id}/claim`, { email: leadEmail, newsletter: leadNews });
      window.open(`${API}/guides/${lead.id}/download`, "_blank");
      toast.success("Ecco la tua guida! Si è aperta in una nuova scheda.");
      setLead(null);
    } catch {
      toast.error("Controlla l'indirizzo email e riprova.");
    } finally {
      setLeadSending(false);
    }
  };

  const buy = async (g) => {
    setBuying(g.id);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: g.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Pagamento non disponibile al momento. Riprova.");
      setBuying(null);
    }
  };

  return (
    <section id="risorse" className="py-20 lg:py-32" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="03" label="Guide gratuite & PDF strumenti" />
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Reveal delay={0.08}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] text-[#1C1E1D] max-w-xl">
              Risorse per iniziare, <em className="text-[#C85A32] not-italic">anche da sola/o</em>
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="flex flex-wrap gap-2" y={20}>
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`resources-tab-${t.id}`}
                      className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                        tab === t.id ? "bg-[#1C211E] text-[#F5F3ED]" : "bg-[#F3EFE6] text-[#525754] hover:bg-[#EAE4D8]"}`}>
                {t.label}
              </button>
            ))}
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-8">
          <p className="text-base text-[#525754] leading-relaxed italic font-serif max-w-xl flex-1">
            «Amo scrivere e condividere ciò che studio: queste guide nascono per offrirti uno spunto, una domanda nuova o un punto di vista diverso su ciò che stai vivendo.»
          </p>
          <img src={IMAGES.writing} alt="Veronica mentre scrive appunti per le sue guide"
               className="w-52 h-36 object-cover rounded-2xl spotlight-img -rotate-2 shrink-0" data-testid="resources-writing-image" />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filtered.map((g, i) => (
            <Reveal key={g.id} delay={0.05 * i} className="h-full">
              <article className="group h-full flex flex-col bg-[#FAF7F0] border border-[#1C1E1D]/8 hover:border-[#1C1E1D]/20 rounded-2xl p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(28,30,29,0.12)]"
                       data-testid={`resource-card-${g.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-wider uppercase px-3 py-1 rounded-full bg-[#EAE4D8] text-[#1C1E1D]">{g.topic}</span>
                  <span className={`font-mono text-xs tracking-wider uppercase px-3 py-1 rounded-full ${g.free ? "bg-[#4A6B5D]/12 text-[#4A6B5D]" : "bg-[#C85A32]/10 text-[#C85A32]"}`}>
                    {g.free ? "Gratuita" : `€ ${g.price.toFixed(2)}`}
                  </span>
                </div>
                <div className="mt-7 flex items-center gap-3 text-[#8A908C]">
                  <FileText size={16} strokeWidth={1.6} />
                  <span className="text-xs">{g.pages} · PDF</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium leading-snug text-[#1C1E1D] mt-3">{g.title}</h3>
                <p className="mt-3 text-sm text-[#525754] leading-relaxed flex-1">{g.description}</p>
                <div className="mt-7 space-y-2.5">
                  {g.free ? (
                    <button onClick={() => { setLead(g); setLeadEmail(""); }} data-testid={`resource-download-${g.id}`}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1C211E] hover:bg-[#4A6B5D] text-[#F5F3ED] text-sm font-medium px-6 py-3 transition-colors duration-300">
                      <Download size={16} /> Scarica la guida gratuita
                    </button>
                  ) : (
                    <button onClick={() => buy(g)} disabled={buying === g.id} data-testid={`resource-buy-${g.id}`}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A32] hover:bg-[#B04B26] disabled:opacity-60 text-[#FBF9F5] text-sm font-medium px-6 py-3 transition-colors duration-300">
                      {buying === g.id ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                      {buying === g.id ? "Reindirizzamento..." : `Acquista · € ${g.price.toFixed(2)}`}
                    </button>
                  )}
                  <button onClick={() => { setPreview(g); setPage(1); }} data-testid={`resource-preview-${g.id}`}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1C1E1D]/20 hover:border-[#C85A32] hover:text-[#C85A32] text-sm font-medium px-6 py-2.5 transition-colors duration-300 text-[#525754]">
                    <Eye size={15} /> Sfoglia l'anteprima
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-[#8A908C] text-center">
            Le guide gratuite si ricevono lasciando la tua email. Gli ebook sono consegnati come PDF subito dopo il pagamento sicuro con Stripe.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[60] bg-[#1C1E1D]/75 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setPreview(null)} data-testid="preview-modal-overlay">
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative bg-[#FBF9F5] rounded-2xl max-w-lg w-full p-5 sm:p-7 shadow-2xl max-h-[92vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
              <button onClick={() => setPreview(null)} aria-label="Chiudi anteprima" data-testid="preview-close"
                      className="absolute top-4 right-4 z-10 p-2 rounded-full bg-[#FBF9F5]/90 hover:bg-[#EAE4D8] transition-colors">
                <X size={18} />
              </button>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#C85A32] mb-1.5">Anteprima gratuita</p>
              <h3 className="font-serif text-lg sm:text-xl font-medium text-[#1C1E1D] pr-10 leading-snug">{preview.title}</h3>
              <div className="mt-4 rounded-xl overflow-hidden border border-[#1C1E1D]/10 shadow-[0_16px_40px_-16px_rgba(28,30,29,0.25)] bg-white">
                <img src={`${API}/guides/${preview.id}/preview/${page}`} alt={`Anteprima pagina ${page} di ${preview.title}`}
                     className="w-full h-auto" data-testid="preview-image" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="preview-prev"
                        className="inline-flex items-center gap-1 rounded-full border border-[#1C1E1D]/15 px-4 py-2 text-sm disabled:opacity-35 hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                  <ChevronLeft size={15} /> Indietro
                </button>
                <span className="font-mono text-xs text-[#8A908C]" data-testid="preview-counter">Pagina {page} di {preview.previews}</span>
                <button onClick={() => setPage((p) => Math.min(preview.previews, p + 1))} disabled={page === preview.previews} data-testid="preview-next"
                        className="inline-flex items-center gap-1 rounded-full border border-[#1C1E1D]/15 px-4 py-2 text-sm disabled:opacity-35 hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                  Avanti <ChevronRight size={15} />
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-[#8A908C]">
                {preview.free ? "Ti piace? Scarica la guida completa, è gratuita." : "Questa è solo un'anteprima: l'ebook completo ha " + preview.pages + "."}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {lead && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[70] bg-[#1C1E1D]/75 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setLead(null)} data-testid="lead-modal-overlay">
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative bg-[#FBF9F5] rounded-2xl max-w-md w-full p-8 sm:p-9 shadow-2xl"
                        onClick={(e) => e.stopPropagation()} data-testid="lead-modal">
              <button onClick={() => setLead(null)} aria-label="Chiudi" data-testid="lead-close"
                      className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#EAE4D8] transition-colors">
                <X size={18} />
              </button>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#C85A32]">Guida gratuita</p>
              <h3 className="font-serif text-2xl font-medium text-[#1C1E1D] mt-2 leading-snug">Dove ti mando la guida?</h3>
              <p className="mt-3 text-sm text-[#525754] leading-relaxed">
                Lasciami la tua email: il download di <strong>{lead.title}</strong> parte subito, e resta il modo più
                semplice per ricevere anche le prossime guide.
              </p>
              <form onSubmit={submitLead} className="mt-6 space-y-4" data-testid="lead-form">
                <input type="email" required value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)}
                       placeholder="La tua email" data-testid="lead-email-input"
                       className="w-full rounded-xl border border-[#1C1E1D]/15 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]/40 focus:border-[#C85A32] transition" />
                <label className="flex items-start gap-3 text-xs text-[#525754] leading-relaxed cursor-pointer">
                  <input type="checkbox" checked={leadNews} onChange={(e) => setLeadNews(e.target.checked)}
                         data-testid="lead-newsletter-checkbox" className="mt-0.5 accent-[#C85A32]" />
                  Sì, voglio ricevere anche riflessioni e strumenti mensili via email (nessuno spam, cancellazione quando vuoi)
                </label>
                <button type="submit" disabled={leadSending} data-testid="lead-submit-button"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A32] hover:bg-[#B04B26] disabled:opacity-60 text-[#FBF9F5] text-sm font-medium px-6 py-3.5 transition-colors duration-300">
                  {leadSending ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {leadSending ? "Un attimo..." : "Scarica la guida"}
                </button>
                <p className="text-[11px] text-[#8A908C] text-center">La tua email è trattata secondo il GDPR e non sarà mai ceduta a terzi.</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
